import React, { useState, useMemo } from 'react';
import { MdFileDownload } from 'react-icons/md';
import './table.css';

/** Narrowest a flexible (wrapping) column may get before the table
 *  stops shrinking and starts scrolling horizontally instead. Roughly
 *  two Arabic words on one line — below this, text starts stacking one
 *  word per line, which is never what anyone wants. */
const DEFAULT_MIN_COLUMN_WIDTH = 120;

/**
 * Table — the new system's one table component, meant to cover
 * everything any list screen in the app needs: sorting, free-text
 * search, per-column filters, a status filter, grouping, pagination,
 * badges, and row actions (edit/delete/custom). One component, one
 * CSS file, no page-specific overrides — a page customizes it only
 * through the `columns` prop, never through its own CSS.
 *
 * Column shape:
 *   {
 *     key: 'name',              // field on the row object
 *     label: 'الاسم',           // header text
 *     width: 2,                 // relative flex share (default 1) — NOT px
 *     sortable: true,           // default true when key is set
 *     filterable: true,         // default: auto (few unique values)
 *     align: 'start'|'center'|'end',
 *     badge: (value, row) => 'success'|'danger'|'warning'|'info'|'neutral',
 *     render: (row) => ReactNode,   // full custom cell, overrides value/badge
 *   }
 *
 * Row actions: pass `onEdit`/`onDelete` for the built-in pair, or
 * `renderActions={(row) => <...>}` for anything custom — both can be
 * used together (custom actions render before edit/delete).
 */
export default function Table({
  columns,
  rows,
  keyField = 'id',
  loading = false,
  emptyText = 'لا توجد بيانات',
  searchPlaceholder = 'بحث...',
  searchableKeys = null, // null = search every column with a key
  statusKey = null, // e.g. 'status' — enables the active/inactive/all filter
  defaultStatus = 'active',
  onEdit,
  onDelete,
  deletingKey = null,
  renderActions = null,
  actionsLabel = 'الإجراءات',
  pageSize = 20,
  exportName = null, // set to enable the CSV export button
  guide, // data-guide name for «ورّيني» (app/help)
}) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const [status, setStatus] = useState(defaultStatus);
  const [colFilters, setColFilters] = useState({});
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);

  const hasActions = !!(onEdit || onDelete || renderActions);

  const filterableColumns = useMemo(
    () => columns.filter((c) => {
      if (c.filterable === false || !c.key) return false;
      if (c.filterable === true) return true;
      const unique = new Set(rows.map((r) => r[c.key]));
      return unique.size > 1 && unique.size <= 12; // auto: a sane dropdown, not a list of ids
    }),
    [columns, rows]
  );

  const filtered = useMemo(() => {
    let data = rows;

    if (statusKey && status !== 'all') {
      data = data.filter((r) => (status === 'active' ? r[statusKey] === 'active' : r[statusKey] !== 'active'));
    }

    for (const [key, value] of Object.entries(colFilters)) {
      if (!value) continue;
      data = data.filter((r) => String(r[key]) === value);
    }

    if (search.trim()) {
      const term = search.trim().toLowerCase();
      const keys = searchableKeys || columns.filter((c) => c.key).map((c) => c.key);
      data = data.filter((r) =>
        keys.some((k) => String(r[k] ?? '').toLowerCase().includes(term))
      );
    }

    return data;
  }, [rows, status, statusKey, colFilters, search, searchableKeys, columns]);

  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    const copy = [...filtered];
    copy.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv), 'ar');
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const currentPage = Math.min(page, totalPages);
  const pageRows = sorted.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: 'asc' };
    });
  };

  /**
   * Export what the user is actually looking at — every filtered and
   * sorted row, not just the current page — as CSV.
   *
   * Two details that matter for Arabic: the BOM, without which Excel
   * reads UTF-8 as mojibake, and CRLF line endings, which Excel needs
   * to keep a quoted field's newlines inside one cell.
   */
  const exportCsv = () => {
    const cols = columns.filter((c) => c.key);
    const cell = (value) => {
      const text = value == null ? '' : String(value);
      return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const lines = [
      cols.map((c) => cell(c.label)).join(','),
      ...sorted.map((row) => cols.map((c) => {
        // exportValue lets a column with a custom render (a badge, an
        // input) still say what its plain text is.
        const raw = c.exportValue ? c.exportValue(row) : row[c.key];
        return cell(c.formatValue && !c.exportValue ? c.formatValue(raw, row) : raw);
      }).join(',')),
    ];
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${exportName}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  /**
   * Column sizing. Three deliberate rules, each fixing a real complaint:
   *
   * 1. A column grows to its share of SPARE space only — it never
   *    shrinks below the width its content needs (`flex-shrink: 0`,
   *    basis `auto`). That's why a phone number can no longer be
   *    broken onto two lines.
   * 2. `grow: 0` columns (nowrap / actions) keep their natural size
   *    entirely, so buttons never get squeezed narrower.
   * 3. Leftover width is absorbed by a trailing spacer, not by
   *    stretching the columns — an almost-empty table stays compact
   *    and just leaves white space, as asked.
   */
  /**
   * ONE grid for the WHOLE table, not one per row.
   *
   * The rows use `display: contents` (see table.css) so every cell is a
   * direct child of this single grid. That is what guarantees
   * alignment: giving each row its own grid — even with an identical
   * template string — still lets `max-content` resolve against that
   * row's own content, so the header's short "رقم الهاتف" produced a
   * narrower track than the body's phone number and every column after
   * it drifted. With one shared grid, a track is sized once from all
   * the cells in that column, header included.
   *
   * Track sizing per column:
   *   nowrap   -> max-content        (phone/date/number: always one line)
   *   width: n -> minmax(MIN, n fr)  (shares leftover width, but NEVER
   *                                   shrinks below MIN)
   *   actions  -> max-content        (buttons keep full size)
   *   trailing -> 1fr only when no flexible column exists, so a narrow
   *               table leaves leftover space empty instead of
   *               stretching its columns across it.
   *
   * The MIN floor is the important part. With `minmax(0, …)` a column
   * could shrink to nothing, so on a narrow window the name column
   * collapsed and wrapped to four lines while the grid still claimed to
   * "fit" — which also meant the horizontal scrollbar never appeared.
   * A real minimum makes the grid genuinely wider than its container
   * when there isn't room, and .ui-table-scroll then scrolls, which is
   * the correct behaviour: shrink to a sensible point, then scroll.
   */
  const gridTemplate = useMemo(() => {
    const tracks = columns.map((c) => {
      if (c.nowrap) return 'max-content';
      const min = c.minWidth || DEFAULT_MIN_COLUMN_WIDTH;
      return `minmax(${min}px, ${c.width || 1}fr)`;
    });
    if (hasActions) tracks.push('max-content');
    const hasFlexible = columns.some((c) => !c.nowrap);
    tracks.push(hasFlexible ? '0px' : '1fr');
    return tracks.join(' ');
  }, [columns, hasActions]);

  return (
    <div className="ui-table-block" data-guide={guide}>
      <div className="ui-table-toolbar">
        <input
          type="search"
          className="ui-table-search"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />

        <div className="ui-table-toolbar-right">
          {statusKey && (
            <div className="ui-segmented" role="tablist">
              {[
                { v: 'active', label: 'نشط' },
                { v: 'inactive', label: 'غير نشط' },
                { v: 'all', label: 'الكل' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  className={`ui-segmented-btn ${status === opt.v ? 'is-active' : ''}`}
                  onClick={() => { setStatus(opt.v); setPage(1); }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          {filterableColumns.map((c) => {
            const options = [...new Set(rows.map((r) => r[c.key]))].filter((v) => v != null);
            return (
              <select
                key={c.key}
                className="ui-table-filter-select"
                value={colFilters[c.key] || ''}
                onChange={(e) => { setColFilters((p) => ({ ...p, [c.key]: e.target.value })); setPage(1); }}
              >
                <option value="">{c.label}: الكل</option>
                {options.map((v) => (
                  <option key={String(v)} value={v}>
                    {c.badge ? (c.formatValue ? c.formatValue(v) : String(v)) : (c.formatValue ? c.formatValue(v) : String(v))}
                  </option>
                ))}
              </select>
            );
          })}

          {exportName && (
            <button
              type="button"
              className="ui-table-export"
              onClick={exportCsv}
              disabled={sorted.length === 0}
              title="تصدير الصفوف المعروضة إلى ملف CSV"
            >
              <MdFileDownload size={16} />
              تصدير
            </button>
          )}
        </div>
      </div>

      <div className="ui-table-scroll">
        <div className="ui-table" role="table" style={{ gridTemplateColumns: gridTemplate }}>
          <div className="ui-table-row ui-table-head" role="row">
            {columns.map((c) => (
              <div
                key={c.key || c.label}
                role="columnheader"
                className={`ui-table-cell ui-table-headcell align-${c.align || 'start'}${c.key ? ' is-sortable' : ''}${c.nowrap ? ' is-nowrap' : ''}`}
                onClick={() => c.key && (c.sortable !== false) && toggleSort(c.key)}
              >
                {/* Label + arrow are one inline group so the header text
                    always sits on the same edge as the data beneath it
                    (an end-aligned number column gets an end-aligned
                    header, not a label pushed away by the arrow). */}
                <span className="ui-headcell-inner">
                  <span>{c.label}</span>
                  {c.key && c.sortable !== false && (
                    <span className="ui-sort-indicator">
                      {sort.key === c.key ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}
                    </span>
                  )}
                </span>
              </div>
            ))}
            {hasActions && (
              <div className="ui-table-cell ui-table-headcell align-center">
                {actionsLabel}
              </div>
            )}
            <div className="ui-table-spacer" aria-hidden="true" />
          </div>

          {loading ? (
            <TableSkeleton columns={columns} hasActions={hasActions} />
          ) : pageRows.length === 0 ? (
            <div className="ui-table-status">{search ? 'لا توجد نتائج مطابقة' : emptyText}</div>
          ) : (
            pageRows.map((row) => (
              <div className="ui-table-row" role="row" key={row[keyField]}>
                {columns.map((c) => (
                  <div
                    key={c.key || c.label}
                    role="cell"
                    className={`ui-table-cell align-${c.align || 'start'}${c.nowrap ? ' is-nowrap' : ''}`}
                    data-label={c.label}
                  >
                    {renderCell(c, row)}
                  </div>
                ))}
                {hasActions && (
                  <div className="ui-table-cell align-center ui-table-actions" data-label={actionsLabel}>
                    {renderActions && renderActions(row)}
                    {onEdit && (
                      <button type="button" className="ui-icon-btn ui-icon-btn-edit" title="تعديل" onClick={() => onEdit(row)} data-guide="row-edit">
                        ✎
                      </button>
                    )}
                    {onDelete && (
                      <button
                        type="button"
                        className="ui-icon-btn ui-icon-btn-delete"
                        title="حذف"
                        disabled={deletingKey === row[keyField]}
                        onClick={() => onDelete(row)}
                      >
                        {deletingKey === row[keyField] ? '…' : '✕'}
                      </button>
                    )}
                  </div>
                )}
                <div className="ui-table-spacer" aria-hidden="true" />
              </div>
            ))
          )}
        </div>
      </div>

      {!loading && sorted.length > 0 && (
        <div className="ui-table-footer">
          <span className="ui-table-count">
            {sorted.length} نتيجة
          </span>
          <div className="ui-table-pager">
            <select
              className="ui-table-filter-select"
              value={rowsPerPage}
              onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
            >
              {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n} / صفحة</option>)}
            </select>
            <button type="button" className="ui-pager-btn" disabled={currentPage <= 1} onClick={() => setPage(1)}>»</button>
            <button type="button" className="ui-pager-btn" disabled={currentPage <= 1} onClick={() => setPage((p) => p - 1)}>›</button>
            <span className="ui-pager-label">{currentPage} / {totalPages}</span>
            <button type="button" className="ui-pager-btn" disabled={currentPage >= totalPages} onClick={() => setPage((p) => p + 1)}>‹</button>
            <button type="button" className="ui-pager-btn" disabled={currentPage >= totalPages} onClick={() => setPage(totalPages)}>«</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Bar widths cycle through a few values instead of all filling 100% —
// a row of identical gray blocks reads as a placeholder graphic, varied
// widths read as "text is about to appear here".
const SKELETON_BAR_WIDTHS = [88, 62, 74, 45, 80, 55];

/**
 * Loading placeholder — rows shaped exactly like real ones (one
 * .ui-table-cell per column, same grid tracks via the shared
 * .ui-table grid) so they line up with the real header instead of a
 * single "جارٍ التحميل..." line floating under it.
 */
function TableSkeleton({ columns, hasActions }) {
  return (
    <>
      {[0, 1, 2, 3, 4].map((rowIndex) => (
        <div className="ui-table-row ui-table-row-skeleton" role="row" key={rowIndex} aria-hidden="true">
          {columns.map((c, colIndex) => (
            <div key={c.key || c.label} role="cell" className={`ui-table-cell align-${c.align || 'start'}`}>
              <span
                className="ui-skeleton-bar"
                style={{ width: `${SKELETON_BAR_WIDTHS[(rowIndex + colIndex) % SKELETON_BAR_WIDTHS.length]}%` }}
              />
            </div>
          ))}
          {hasActions && (
            <div role="cell" className="ui-table-cell align-center ui-table-actions">
              <span className="ui-skeleton-bar ui-skeleton-bar-action" />
            </div>
          )}
          <div className="ui-table-spacer" aria-hidden="true" />
        </div>
      ))}
    </>
  );
}

function renderCell(column, row) {
  if (column.render) return column.render(row);
  const value = row[column.key];
  const display = column.formatValue ? column.formatValue(value, row) : (value ?? '—');
  if (column.badge) {
    const tone = column.badge(value, row) || 'neutral';
    return <span className={`ui-badge ui-badge-${tone}`}>{display}</span>;
  }
  const text = display === '' || display == null ? '—' : display;
  // `clamp: n` caps a long free-text column at n lines instead of
  // letting one long note stretch the whole row.
  if (column.clamp) {
    return (
      <span className="ui-clamp" style={{ WebkitLineClamp: column.clamp }}>
        {text}
      </span>
    );
  }
  return text;
}
